// src/pages/JobDetailPage.tsx
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { API_BASE_URL } from '../config';
import { sanitizeHtml, stripHtml } from '../utils/html';
import '../styles/JobDetailPage.css';

interface Job {
  _id: string;
  title: string;
  location: string;
  employmentType: string;
  salaryRange?: string;
  description: string;
  responsibilities?: string;
  requirements?: string;
  applicationEmail: string;
  deadline?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

const EMPLOYMENT_TYPE_LABELS: Record<string, string> = {
  'full-time': 'Full-time',
  'part-time': 'Part-time',
  'contract': 'Contract',
  'internship': 'Internship',
  'volunteer': 'Volunteer',
};

const JobDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchJob = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/jobs/${id}`);

      if (!response.ok) {
        throw new Error('Failed to fetch job');
      }

      const data = await response.json();
      setJob(data);
    } catch (err) {
      setError('Failed to load job. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const renderLines = (text?: string) =>
    (text || '')
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

  if (loading) {
    return (
      <div className="job-detail-loading">
        <div className="loading-spinner"></div>
        <p>Loading job...</p>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="job-detail-error">
        <h2>Error</h2>
        <p>{error || 'Job not found'}</p>
        <Link to="/careers" className="back-link">Back to Careers</Link>
      </div>
    );
  }

  if (job.status !== 'published') {
    return (
      <div className="job-detail-error">
        <h2>Position Not Available</h2>
        <p>This job posting is not currently open.</p>
        <Link to="/careers" className="back-link">Back to Careers</Link>
      </div>
    );
  }

  const deadlinePassed = job.deadline && new Date(job.deadline).getTime() < Date.now();
  const responsibilities = renderLines(job.responsibilities);
  const requirements = renderLines(job.requirements);

  const mailtoSubject = encodeURIComponent(`Application: ${job.title}`);
  const mailtoBody = encodeURIComponent(
    `Dear EmpowerFuture21 Team,\n\nI would like to apply for the ${job.title} position. Please find my CV attached.\n\nKind regards,\n`
  );

  return (
    <>
      <SEO
        title={`${job.title} — Careers`}
        description={stripHtml(job.description).substring(0, 160)}
        keywords={`job, career, ${job.title}, ${job.employmentType}, vacancy, employment`}
      />

      <div className="job-detail">
        <div className="container">
          <Link to="/careers" className="job-detail-back">← Back to all openings</Link>

          <div className="job-detail-header">
            <h1 className="job-detail-title">{job.title}</h1>
            <div className="job-detail-meta">
              <span className="job-detail-badge">
                {EMPLOYMENT_TYPE_LABELS[job.employmentType] || job.employmentType}
              </span>
              <span className="job-detail-location">📍 {job.location}</span>
              {job.salaryRange && <span className="job-detail-salary">💰 {job.salaryRange}</span>}
              {job.deadline && (
                <span className={`job-detail-deadline ${deadlinePassed ? 'passed' : ''}`}>
                  ⏰ {deadlinePassed ? 'Applications closed' : `Apply by ${formatDate(job.deadline)}`}
                </span>
              )}
            </div>
          </div>

          <div className="job-detail-body">
            <div className="job-detail-content">
              <section className="job-section">
                <h2>About the role</h2>
                <div
                  className="job-description rich-text-content"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(job.description) }}
                />
              </section>

              {responsibilities.length > 0 && (
                <section className="job-section">
                  <h2>Responsibilities</h2>
                  <ul className="job-list">
                    {responsibilities.map((line, i) => <li key={i}>{line}</li>)}
                  </ul>
                </section>
              )}

              {requirements.length > 0 && (
                <section className="job-section">
                  <h2>Requirements</h2>
                  <ul className="job-list">
                    {requirements.map((line, i) => <li key={i}>{line}</li>)}
                  </ul>
                </section>
              )}
            </div>

            <aside className="job-apply-box">
              <h3>How to apply</h3>
              {deadlinePassed ? (
                <p className="apply-closed">Applications for this position are now closed.</p>
              ) : (
                <>
                  <p>
                    Send your CV and a short cover letter to the email below.
                    Please include the job title in your subject line.
                  </p>
                  <a
                    className="apply-btn"
                    href={`mailto:${job.applicationEmail}?subject=${mailtoSubject}&body=${mailtoBody}`}
                  >
                    ✉️ Apply via Email
                  </a>
                  <div className="apply-email">
                    <span>Or email directly:</span>
                    <a href={`mailto:${job.applicationEmail}`}>{job.applicationEmail}</a>
                  </div>
                  {job.deadline && (
                    <p className="apply-deadline-note">Deadline: {formatDate(job.deadline)}</p>
                  )}
                </>
              )}
            </aside>
          </div>
        </div>
      </div>
    </>
  );
};

export default JobDetailPage;
